import { useState } from "react";
import { useCustomers, type Customer } from "@/hooks/use-customers";
import type { CustomerInfo } from "@/hooks/use-project-detail";
import { creditNoteCustomerId } from "@/lib/invoice-customer";

interface UseInvoiceCustomerInput {
  /** Prosjektets hovedkunde. */
  mainCustomerId: string | null;
  /** Hovedkundens kundekort fra prosjektet, brukt før kundelista er lastet. */
  mainCustomer: CustomerInfo | null;
  projectInvoices: ReadonlyArray<{ id: string; customer_id: string | null }>;
  isCreditNote: boolean;
  relatedInvoiceId?: string | null;
}

export type InvoiceCustomer = Pick<
  Customer,
  "id" | "name" | "organization_number" | "contact_person" | "invoice_email_address" | "poweroffice_customer_id"
>;

/**
 * VIB-3030: kunden fakturaen går til. Hovedkunden er valgt fra start. En
 * kreditnota er låst til kunden på fakturaen den krediterer.
 */
export function useInvoiceCustomer({
  mainCustomerId,
  mainCustomer,
  projectInvoices,
  isCreditNote,
  relatedInvoiceId,
}: UseInvoiceCustomerInput) {
  const { data: customers = [] } = useCustomers();
  const [selectedId, setSelectedId] = useState<string | null>(mainCustomerId);

  const locked = isCreditNote ? creditNoteCustomerId(relatedInvoiceId, projectInvoices, mainCustomerId) : undefined;
  const customerId = locked !== undefined ? locked : selectedId;

  const fromList = customerId ? customers.find((c) => c.id === customerId) ?? null : null;
  const customer: InvoiceCustomer | null =
    fromList ?? (customerId && customerId === mainCustomer?.id ? mainCustomer : null);

  return {
    customers,
    customerId,
    customer,
    isMainCustomer: customerId === mainCustomerId,
    /** Kunden kan ikke faktureres før den er koblet til PowerOffice. */
    notInPowerOffice: !!customer && customer.poweroffice_customer_id == null,
    lockedReason: locked !== undefined ? "Samme kunde som fakturaen som krediteres" : null,
    select: setSelectedId,
  };
}
